import PropTypes from 'prop-types';
import React, { forwardRef, useImperativeHandle } from 'react';

import usePresetCueMessage from 'hooks/usePresetCueMessage';
import useWxCopilot from 'hooks/useWxCopilot';
import { useWxCopilotPageContext } from 'hooks/useWxCopilotPageContext';

import { CUE_PANEL_ROOT_MOUNT_ID, CUE_TRIGGER_MOUNT_ID } from './constants';

// Default mount ids by mode. The panel-root id is the historical
// 'wx-copilot-root' so existing hooks that default to it (e.g.
// useWxCopilotPageContext) continue to target the right instance.
function defaultMountIdForMode(mode) {
  if (mode === 'trigger') return CUE_TRIGGER_MOUNT_ID;
  return CUE_PANEL_ROOT_MOUNT_ID;
}

// eslint-disable-next-line react/prop-types
function WxCopilotHostInner(props, ref) {
  // eslint-disable-next-line react/prop-types
  const { id, mode = 'full', ...renderProps } = props;
  const mountId = id || defaultMountIdForMode(mode);

  // Forward `mode` to the UMD render call so the bundle picks the right
  // surface (full / trigger / panel-root).
  useWxCopilot({
    mountId,
    renderProps: { mode, ...renderProps },
  });

  // Page context and preset-message wiring only make sense against the
  // surface that owns the InternalBridge — i.e. the panel-root (or 'full'
  // back-compat mount). Trigger mounts have no InternalBridge and ignore
  // these dispatches.
  const isCommandSink = mode === 'panel-root' || mode === 'full';
  useWxCopilotPageContext(isCommandSink ? mountId : null);
  usePresetCueMessage(isCommandSink ? mountId : null);

  useImperativeHandle(
    ref,
    () => ({
      sendMessageToQueue: payload =>
        window.WxCopilotComponentInstances?.[mountId]?.sendMessageToQueue?.(
          payload,
        ),
      toggleCopilotPanel: () => {
        // Prefer the global store API (mode-agnostic; works whether this
        // host owns the panel or just a trigger). Fall back to the legacy
        // per-instance command for older bundles.
        if (window.WxCopilotComponent?.togglePanel) {
          window.WxCopilotComponent.togglePanel();
          return;
        }
        window.WxCopilotComponentInstances?.[mountId]?.toggleCopilotPanel?.();
      },
      destroy: () => window.WxCopilotComponentInstances?.[mountId]?.destroy?.(),
    }),
    [mountId],
  );

  return null;
}

const WxCopilotHost = forwardRef(WxCopilotHostInner);
WxCopilotHost.propTypes = {
  id: PropTypes.string,
  mode: PropTypes.oneOf(['full', 'trigger', 'panel-root']),
  companyUuid: PropTypes.string,
  product: PropTypes.string,
  panelClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  buttonStyle: PropTypes.object,
  locale: PropTypes.string,
  openMethod: PropTypes.string,
  onPanelEvent: PropTypes.func,
  onToolEvent: PropTypes.func,
};

// IMPORTANT: When embedding the UMD component inside a React app, wrap the host wrapper
// with React.memo to prevent parent re-renders from accidentally remounting the provider
// tree and Copilot internals.
const MemoWxCopilotHost = React.memo(WxCopilotHost);
export default MemoWxCopilotHost;
